import React, {
  FC,
  useMemo,
  useCallback,
  useRef,
  useLayoutEffect,
} from 'react';
import { Link } from 'react-router-dom';
import { EadNode } from 'types';
import { useEadTreeContext } from './EadTreeCtx';

import {
  Button,
  Theme,
  Tooltip,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { LevelDefaultIcon, levelIcon } from '../grid-hooks/breadcrumbs-hook';
import { getNodeId, isInPath } from './ctx-utils';
import { useVariantContext, variantToPath } from './VariantCtx';
import { fetchEadChildren } from './fetch-with-cache';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    wrapper: {
      paddingLeft: 15,
      paddingTop: 8,
      paddingBottom: 8,
      borderLeft: '1px dashed #a3a3a370',
      borderBottom: '1px dashed #afafaf70',
      color: 'white',
    },
    flex: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      border: `1px solid ${theme.palette.secondary.main}`,
      borderRadius: 4,
    },
    expandButton: {
      minWidth: 25,
      maxWidth: 25,
      height: 20,
      padding: 2,
      margin: '0 8px 0 0',
      color: '#a3a3a3',
      borderColor: '#a3a3a3',
      '&:hover': {
        borderColor: '#fff',
        color: '#fff',
      },
    },
    eadItem: {
      backgroundColor: 'transparent',
      textOverflow: 'ellipsis',
      display: 'block',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
    active: {
      backgroundColor: theme.palette.primary.dark,
      color: 'white!important',
      padding: '0 4px 0 0px',
    },
    link: {
      color: 'white',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    placeholder: {
      minWidth: 25,
      maxWidth: 25,
      padding: 2,
      margin: '0 8px 0 0',
    },
    icon: {
      marginRight: 4,
      fontSize: 13,
    },
    tooltip: {
      fontSize: 12,
    },
  })
);

type Props = {
  level: number;
  node: EadNode;
};

const EadItem: FC<Props> = ({ node, level = 1 }) => {
  const {
    childrenViewHeap,
    currentEadNode,
    setChildrenViewHeap,
    expandedNodesIdsHEAP,
    setExpandedNodesIdsHEAP,
    setNextIdsToExpand,
    setLoading,
    crumbs,
  } = useEadTreeContext();

  const nodeId = useMemo(() => getNodeId(node), [node]);
  const variant = useVariantContext();
  const children = useMemo(
    () => childrenViewHeap?.[nodeId!] ?? node.children ?? [],
    [childrenViewHeap, node, nodeId]
  );
  const isActive = useMemo(() => nodeId === getNodeId(currentEadNode), [
    nodeId,
    currentEadNode,
  ]);
  const classes = useStyles();

  const expanded = useMemo(
    () => expandedNodesIdsHEAP && nodeId && expandedNodesIdsHEAP[nodeId],
    [expandedNodesIdsHEAP, nodeId]
  );

  const handleExpand = useCallback(async () => {
    if (!nodeId) {
      return;
    }
    setLoading?.(true);

    if (!expanded) {
      const nodeWithChildren = await fetchEadChildren([nodeId]);
      setChildrenViewHeap?.((p) => ({
        ...p,
        [nodeId]: nodeWithChildren?.[0].children ?? [],
      }));

      if (isInPath(nodeId, crumbs ?? [])) {
        const toExpand = nodeWithChildren
          .map((n) =>
            (n.children ?? [])
              ?.filter((cc) => cc.childrenCount > 0)
              .map((c) => getNodeId(c)!)
          )
          .flat();
        setNextIdsToExpand?.((p) => [...p, ...toExpand]);
      }
      setExpandedNodesIdsHEAP?.((p) => {
        if (nodeId && p[nodeId]) return p;
        if (nodeId) {
          return { ...p, [nodeId]: true };
        }
        return p;
      });
    } else {
      setExpandedNodesIdsHEAP?.((p) => ({ ...p, [nodeId]: false }));
    }
    // listRef.current?.recomputeRowHeights();
    // listRef.current?.forceUpdate();
    setLoading?.(false);
  }, [
    nodeId,
    setLoading,
    expanded,
    setChildrenViewHeap,
    crumbs,
    setExpandedNodesIdsHEAP,
    setNextIdsToExpand,
  ]);
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (isActive) {
      setTimeout(() => {
        const el = document.getElementById('G_LEFT_PANEL');
        const top = (ref.current?.offsetTop ?? 0) - 100;
        el?.scrollTo({
          top,
          behavior: 'smooth',
        });
      }, 10);
    }
  }, [ref, isActive]);

  const Icon = node.level
    ? levelIcon?.[node.level] ?? LevelDefaultIcon
    : LevelDefaultIcon;

  return (
    <div className={classes.wrapper}>
      <div
        className={`${classes.flex} ${isActive ? classes.active : ''}`}
        ref={ref}
      >
        {node.childrenCount > 0 ? (
          <Tooltip
            title={
              <span className={classes.tooltip}>
                {expanded ? 'Sbalit' : 'Rozbalit'}
              </span>
            }
            placement="top"
            enterDelay={500}
            enterNextDelay={500}
          >
            <Button
              onClick={handleExpand}
              variant="outlined"
              className={classes.expandButton}
            >
              {expanded ? '-' : '+'}
            </Button>
          </Tooltip>
        ) : (
          <span className={classes.placeholder} />
        )}
        <span>
          <Tooltip
            title={<span className={classes.tooltip}>{node.levelLabel}</span>}
            placement="top"
          >
            <Icon className={classes.icon} />
          </Tooltip>
        </span>
        <span
          className={`${classes.eadItem} ${isActive ? classes.active : ''}`}
        >
          <Link
            className={`${classes.link} ${isActive ? classes.active : ''}`}
            to={`/${variantToPath[variant!]}/${level}/${nodeId}/ead`}
          >
            {node.title}
          </Link>
        </span>
      </div>
      {expanded && children.map((c) => <EadItem level={level + 1} node={c} />)}
    </div>
  );
};

export default EadItem;
